<template>
  <div class="roles-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="roles-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-header-left">
          <div class="page-icon-box">
            <v-icon color="#F2F2ED" size="20">mdi-shield-account-outline</v-icon>
          </div>
          <div>
            <h1 class="page-title">Roles y permisos</h1>
            <p class="page-sub">Define qué puede ver y hacer cada rol dentro del sistema</p>
          </div>
        </div>
        <button class="btn-primary" @click="nuevoRol">
          <v-icon size="15" class="mr-2">mdi-plus</v-icon>
          Nuevo rol
        </button>
      </header>

      <!-- Lista de roles -->
      <section class="fade-in delay-100">
        <div v-if="cargando" class="loading-state">
          <div class="spinner"></div>
          <p class="loading-text">Cargando roles…</p>
        </div>

        <div v-else class="roles-grid">
          <div v-for="rol in roles" :key="rol.id" class="rol-card">
            <div class="rol-head">
              <div class="rol-icon">
                <v-icon size="18" color="#C8FF4D">mdi-shield-account</v-icon>
              </div>
              <div class="flex-1">
                <h3 class="rol-name">{{ rol.nombre }}</h3>
                <p class="rol-desc">{{ rol.descripcion || 'Sin descripción' }}</p>
              </div>
            </div>

            <div class="rol-meta">
              <span class="status-tag status-tag-neutral">
                <v-icon size="11" class="mr-1">mdi-account-multiple</v-icon>
                {{ rol.total_usuarios }} usuario{{ rol.total_usuarios === 1 ? '' : 's' }}
              </span>
            </div>

            <div class="rol-actions">
              <button class="btn-ghost btn-sm" @click="editarRol(rol)">
                <v-icon size="13" class="mr-1">mdi-pencil</v-icon>
                Editar permisos
              </button>
              <button class="icon-btn icon-btn-danger" @click="confirmarEliminar(rol)" title="Eliminar rol">
                <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Diálogo: Crear/editar rol -->
    <v-dialog v-model="mostrarDialogo" persistent max-width="640px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">{{ rolEditando ? 'Editar rol' : 'Nuevo rol' }}</h3>
          <p class="modal-sub">Marca los módulos que este rol podrá ver y usar</p>
        </div>

        <div class="modal-body">
          <div class="field-row">
            <div class="field">
              <label class="field-label">Nombre del rol *</label>
              <input v-model="formRol.nombre" type="text" class="field-input" placeholder="Ej. Recepcionista" />
            </div>
            <div class="field">
              <label class="field-label">Descripción</label>
              <input v-model="formRol.descripcion" type="text" class="field-input" placeholder="Opcional" />
            </div>
          </div>

          <div class="section-divider"></div>

          <div class="permisos-header">
            <span class="mini-eyebrow">Permisos del rol</span>
            <button class="link-btn" @click="toggleTodos">
              {{ todosMarcados ? 'Desmarcar todos' : 'Marcar todos' }}
            </button>
          </div>

          <div v-for="(permisosGrupo, modulo) in permisosAgrupados" :key="modulo" class="permiso-grupo">
            <div class="permiso-grupo-head">
              <span class="permiso-grupo-title">{{ modulo }}</span>
              <button class="link-btn-sm" @click="toggleGrupo(permisosGrupo)">
                {{ grupoCompleto(permisosGrupo) ? 'Ninguno' : 'Todos' }}
              </button>
            </div>
            <div class="permiso-list">
              <label v-for="permiso in permisosGrupo" :key="permiso.id" class="permiso-check-row">
                <input type="checkbox" :value="permiso.id" v-model="formRol.permisos" class="check-input" />
                <span class="check-label">{{ permiso.nombre }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="cerrarDialogo">Cancelar</button>
          <button class="btn-primary" :disabled="!formRol.nombre || guardando" @click="guardarRol">
            <span v-if="!guardando">{{ rolEditando ? 'Guardar cambios' : 'Crear rol' }}</span>
            <span v-else class="loading-inline"><span class="spinner-sm"></span>Guardando</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Diálogo: Eliminar -->
    <v-dialog v-model="mostrarDialogoEliminar" max-width="460px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Confirmar eliminación</h3>
        </div>
        <div class="modal-body">
          <p class="modal-text">
            ¿Eliminar el rol <strong class="text-error">"{{ rolAEliminar.nombre }}"</strong>?
          </p>
          <div v-if="rolAEliminar.total_usuarios > 0" class="alert-box">
            <v-icon size="18" color="#FFB84D">mdi-alert</v-icon>
            <div class="alert-text">
              Este rol tiene {{ rolAEliminar.total_usuarios }} usuario(s) asignado(s). No se puede eliminar hasta
              que los reasignes a otro rol.
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="mostrarDialogoEliminar = false">Cancelar</button>
          <button class="btn-danger-solid" :disabled="rolAEliminar.total_usuarios > 0 || eliminando" @click="eliminarRolConfirmado">
            <span v-if="!eliminando">Eliminar</span>
            <span v-else class="loading-inline"><span class="spinner-sm"></span>Eliminando</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="3000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService';

export default {
  name: "GestionRoles",

  data: () => ({
    cargando: false,
    guardando: false,
    eliminando: false,
    roles: [],
    permisos: [],
    mostrarDialogo: false,
    mostrarDialogoEliminar: false,
    rolEditando: null,
    rolAEliminar: {},
    formRol: {
      id: null,
      nombre: '',
      descripcion: '',
      permisos: []
    },
    mostrarMensaje: false,
    mensaje: { texto: '', color: '' }
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle';
        case 'error': return 'mdi-alert-circle';
        case 'warning': return 'mdi-alert';
        default: return 'mdi-information';
      }
    },

    // Agrupa los permisos por módulo, en el mismo orden que vienen de
    // la base de datos (ORDER BY modulo, orden en obtenerPermisos()).
    permisosAgrupados() {
      const grupos = {};
      this.permisos.forEach(p => {
        if (!grupos[p.modulo]) grupos[p.modulo] = [];
        grupos[p.modulo].push(p);
      });
      return grupos;
    },

    todosMarcados() {
      return this.permisos.length > 0 && this.formRol.permisos.length === this.permisos.length;
    }
  },

  mounted() {
    this.cargarRoles();
    this.cargarPermisos();
  },

  methods: {
    async cargarRoles() {
      this.cargando = true;
      try {
        this.roles = await HttpService.obtenerConDatos({ metodo: 'get' }, 'roles.php');
      } catch (error) {
        this.mostrarError('Error al cargar los roles');
      } finally {
        this.cargando = false;
      }
    },

    async cargarPermisos() {
      try {
        this.permisos = await HttpService.obtenerConDatos({ metodo: 'get_permisos' }, 'roles.php');
      } catch (error) {
        this.mostrarError('Error al cargar los permisos');
      }
    },

    nuevoRol() {
      this.rolEditando = null;
      this.formRol = { id: null, nombre: '', descripcion: '', permisos: [] };
      this.mostrarDialogo = true;
    },

    async editarRol(rol) {
      this.rolEditando = rol;
      try {
        const detalle = await HttpService.obtenerConDatos({ metodo: 'get_by_id', id: rol.id }, 'roles.php');
        this.formRol = {
          id: detalle.id,
          nombre: detalle.nombre,
          descripcion: detalle.descripcion || '',
          permisos: detalle.permisos || []
        };
        this.mostrarDialogo = true;
      } catch (error) {
        this.mostrarError('Error al cargar el rol');
      }
    },

    cerrarDialogo() {
      this.mostrarDialogo = false;
      this.rolEditando = null;
    },

    toggleTodos() {
      this.formRol.permisos = this.todosMarcados ? [] : this.permisos.map(p => p.id);
    },

    grupoCompleto(permisosGrupo) {
      return permisosGrupo.every(p => this.formRol.permisos.includes(p.id));
    },

    toggleGrupo(permisosGrupo) {
      const idsGrupo = permisosGrupo.map(p => p.id);
      if (this.grupoCompleto(permisosGrupo)) {
        this.formRol.permisos = this.formRol.permisos.filter(id => !idsGrupo.includes(id));
      } else {
        this.formRol.permisos = [...new Set([...this.formRol.permisos, ...idsGrupo])];
      }
    },

    async guardarRol() {
      this.guardando = true;
      const metodo = this.rolEditando ? 'put' : 'post';

      try {
        const respuesta = await HttpService.registrar({ metodo, rol: this.formRol }, 'roles.php');

        if (respuesta && respuesta.success) {
          this.mostrarExito(respuesta.message);
          this.cerrarDialogo();
          await this.cargarRoles();
        } else {
          this.mostrarError((respuesta && respuesta.message) || 'Error al guardar el rol');
        }
      } catch (error) {
        this.mostrarError('Error al guardar el rol');
      } finally {
        this.guardando = false;
      }
    },

    confirmarEliminar(rol) {
      this.rolAEliminar = rol;
      this.mostrarDialogoEliminar = true;
    },

    async eliminarRolConfirmado() {
      this.eliminando = true;
      try {
        const respuesta = await HttpService.eliminar('roles.php', { metodo: 'delete', id: this.rolAEliminar.id });

        if (respuesta && respuesta.success) {
          this.mostrarExito(respuesta.message);
          this.mostrarDialogoEliminar = false;
          await this.cargarRoles();
        } else {
          this.mostrarError((respuesta && respuesta.message) || 'Error al eliminar el rol');
        }
      } catch (error) {
        this.mostrarError('Error al eliminar el rol');
      } finally {
        this.eliminando = false;
      }
    },

    mostrarExito(texto) {
      this.mostrarMensaje = true;
      this.mensaje = { texto, color: 'success' };
    },

    mostrarError(texto) {
      this.mostrarMensaje = true;
      this.mensaje = { texto, color: 'error' };
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

.roles-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #FFB84D;
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.roles-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.roles-shell {
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  gap: 16px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.spinner-sm {
  width: 11px;
  height: 11px;
  border: 2px solid rgba(0, 0, 0, 0.25);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}

.rol-card {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 18px;
}

.rol-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.rol-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius);
  background: var(--accent-dim);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rol-name {
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 3px;
}

.rol-desc {
  font-size: 12px;
  color: var(--text-tertiary);
  margin: 0;
  line-height: 1.4;
}

.rol-meta {
  margin-bottom: 14px;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 600;
  font-family: var(--font-mono);
}

.status-tag-neutral {
  background: rgba(140, 140, 147, 0.14);
  color: var(--text-secondary);
}

.rol-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid var(--card-border);
}

.icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
  color:#fc2323;
}

.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 4px;
}

.modal-sub {
  font-size: 12.5px;
  color: var(--text-secondary);
  margin: 0;
}

.modal-body {
  padding: 24px;
  max-height: 62vh;
  overflow-y: auto;
}

.modal-text {
  font-size: 13.5px;
  color: var(--text-secondary);
  margin: 0 0 14px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.field-input {
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 11px 14px;
  font-size: 14px;
  font-family: var(--font-body);
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.15s ease;
}

.field-input::placeholder {
  color: var(--text-tertiary);
}

.field-input:focus {
  border-color: var(--accent);
}

.section-divider {
  height: 1px;
  background: var(--card-border);
  margin: 20px 0;
}

.permisos-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.mini-eyebrow {
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

.link-btn {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.link-btn:hover {
  text-decoration: underline;
}

.permiso-grupo {
  margin-bottom: 16px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 12px 14px;
}

.permiso-grupo-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.permiso-grupo-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.link-btn-sm {
  background: none;
  border: none;
  color: var(--text-tertiary);
  font-size: 11px;
  cursor: pointer;
}

.link-btn-sm:hover {
  color: var(--accent);
}

.permiso-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
}

.permiso-check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.check-input {
  width: 15px;
  height: 15px;
  accent-color: var(--accent);
  cursor: pointer;
}

.check-label {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.alert-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 184, 77, 0.08);
  border: 1px solid rgba(255, 184, 77, 0.25);
  border-radius: var(--radius);
  padding: 12px 14px;
  margin-top: 12px;
}

.alert-text {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: default;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-sm {
  padding: 7px 12px;
  font-size: 12px;
}

.btn-danger-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--error);
  color: #ff0202;
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-danger-solid:hover:not(:disabled) {
  filter: brightness(1.1);
}

.btn-danger-solid:disabled {
  opacity: 0.4;
  cursor: default;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner, .spinner-sm { animation: none; }
}

@media (max-width: 600px) {
  .roles-page {
    padding: 20px;
  }

  .field-row {
    grid-template-columns: 1fr;
  }

  .permiso-list {
    grid-template-columns: 1fr;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Usuarios/GestionRoles.vue